import type { Slide } from "../types";

export const SLIDES: Slide[] = [
    // "What is a carbon footprint?"
    {
        kind: "introCard",
        id: "welcome",
        route: "/welcome",
        headline: "What is a carbon footprint?",
        body: `
        <p>It is the measure of carbon emitted during the creation, transportation, use and/or disposal of a given product or good.</p>
        <p class="mt-4 font-bold"> Here's an example:</p>
        `,
        mainImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/cereal-co2e_v02.webp",
            alt: "Cereal box with CO2e in kg",
        },
        ctaLabel: "Start",
    },

    // Q1
    {
        kind: "questionIntro",
        id: "q1-intro",
        route: "/q1-intro",
        headline: `Let's <span style="display: inline-block" data-anim="kick">kick</span> things off with a question about footwear.`,
        questionId: "q1",
    },
    {
        kind: "question",
        id: "q1",
        route: "/q1",
        questionId: "q1",
        category: "Shoes",
        questionText:
            "What is the carbon footprint of <br class='md:hidden'/> a typical pair of shoes?",
        hint: "HINT: One gallon of gasoline is equal to about 8.9 kg CO<sub>2</sub>e.",
        image: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/shoes.webp",
            alt: "Shoes",
        },
        scoreImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/spotlight_shoes.webp",
            alt: "diaper",
        },
        choices: [
            { label: "3.1 kg", isCorrect: false },
            { label: "49.5 kg", isCorrect: false },
            { label: "13.6 kg", isCorrect: true },
            { label: "0.4 kg", isCorrect: false },
        ],
        correctValue: "13.6 kg",
        feedback: {
            headlineCorrect: "Good Answer!",
            headlineIncorrect: "Not Quite!",
            spanCorrect: "Well done—you're off to a running start!",
            spanIncorrect: "No worries—let's jog on over to the next question!",
            image: {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/shoes.webp",
                alt: "shoes",
            },
            bodyHtml: `
              <h3>A typical pair of shoes <br class='md:hidden'/> has a carbon footprint of <br class='md:hidden'/> about 13.6 kg CO<sub>2</sub>e</h3>
              <p>That's equivalent to using 1.5 gallons of gasoline just to make one pair. Most of that
              impact comes from manufacturing and materials like leather or synthetics. <span data-span></span></p>
            `,
            ctaLabel: "Next Question",
        },
    },

    // Q2
    {
        kind: "questionIntro",
        id: "q2-intro",
        route: "/q2-intro",
        headline:
            "Let's start the next round with a classic <span data-anim='pick-me-up'>pick-me-up</span>.",
        questionId: "q2",
    },
    {
        kind: "question",
        id: "q2",
        route: "/q2",
        questionId: "q2",
        category: "Coffee",
        questionText:
            "What is the carbon footprint of <br class='md:hidden'/> an average cup of coffee?​",
        hint: "HINT: Charging your phone once releases <br class='md:hidden'/> about 0.0125 kg CO<sub>2</sub>e.",
        image: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/coffee.webp",
            alt: "cup of cofee",
        },
        scoreImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/spotlight_coffee.webp",
            alt: "diaper",
        },
        choices: [
            { label: "0.2 kg", isCorrect: true },
            {
                label: "1.5 kg",
                isCorrect: false,
            },
            {
                label: "7.3 kg",
                isCorrect: false,
            },
            {
                label: "3.7 kg",
                isCorrect: false,
            },
        ],
        correctValue: "0.203 kg",
        feedback: {
            headlineCorrect: "Spot On!",
            headlineIncorrect: "Close!",
            spanCorrect: "You're certainly charged up for the day!",
            spanIncorrect: "Let's get perked up for the next question!",
            image: {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/coffee.webp",
                alt: "cup of cofee",
            },
            bodyHtml: `
                <h3>A typical cup of coffee <br class='md:hidden'/> has a carbon footprint of <br class='md:hidden'/> about 0.2 kg CO<sub>2</sub>e</h3>
                <p>That's equivalent to the carbon emissions from charging a smartphone 16 times. Most of
                    the emissions come from growing, processing and transporting the beans. 
                    <span data-span></span>                
                </p>
                `,
            ctaLabel: "Next Question",
        },
    },

    // Q3
    {
        kind: "questionIntro",
        id: "q3-intro",
        route: "/q3-intro",
        headline:
            "Let's move on to a household <span data-anim='staple'>staple</span>.",
        questionId: "q3",
    },
    {
        kind: "question",
        id: "q3",
        route: "/q3",
        questionId: "q3",
        category: "Dish Soap",
        questionText:
            "What is the carbon footprint of <br class='md:hidden'/> a 16 oz bottle of dish soap?​",
        hint: "HINT: One mile of driving releases about 0.37 kg CO<sub>2</sub>e.",
        image: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/dishsoap.webp",
            alt: "bottle of dish soap",
        },
        scoreImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/spotlight_dishsoap.webp",
            alt: "diaper",
        },
        choices: [
            { label: "2.15 kg", isCorrect: false },
            {
                label: "10.21 kg",
                isCorrect: false,
            },
            {
                label: "5.45 kg",
                isCorrect: false,
            },
            {
                label: "0.48 kg",
                isCorrect: true,
            },
        ],
        correctValue: "0.484 kg",
        feedback: {
            headlineCorrect: "You Got It!",
            headlineIncorrect: "Almost!",
            image: {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/dishsoap.webp",
                alt: "bottle of dish soap",
            },
            bodyHtml: `
                  <h3>A typical 16 oz bottle of dish soap <br class='md:hidden'/> has a carbon footprint of <br class='md:hidden'/> about 0.48 kg CO<sub>2</sub>e</h3>
                  <p>That's equivalent to the carbon emissions from driving a car 1.2 miles. Most of that comes from manufacturing and packaging. Let's see how you clean up on this next question.</p>
                `,
            ctaLabel: "Next Question",
        },
    },
    // q4
    {
        kind: "questionIntro",
        id: "q4-intro",
        route: "/q4-intro",
        headline:
            "This next question is about man's <span data-anim='dog'>best friend</span>.",
        questionId: "q4",
    },
    {
        kind: "question",
        id: "q4",
        route: "/q4",
        questionId: "q4",
        category: "Dog Food",
        questionText:
            "What is the carbon footprint of <br class='md:hidden'/> a 30 lb bag of dog food?​",
        hint: "HINT: 100 miles of driving is equal to about 40 kg CO<sub>2</sub>e.",
        image: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/dogfood.webp",
            alt: "bag and bowl of dog food",
        },
        scoreImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/spotlight_dogfood.webp",
            alt: "diaper",
        },
        choices: [
            { label: "115 kg", isCorrect: false },
            {
                label: "200 kg",
                isCorrect: true,
            },
            {
                label: "88 kg",
                isCorrect: false,
            },
            {
                label: "153 kg",
                isCorrect: false,
            },
        ],
        correctValue: "200 kg",
        feedback: {
            headlineCorrect: "Yes!",
            headlineIncorrect: "Not This Time!",
            spanCorrect: "Great work!",
            spanIncorrect: "Don't worry—we'll fetch up the next question.",
            image: {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/dogfood.webp",
                alt: "bag and bowl of dog food",
            },
            bodyHtml: `
                  <h3>A 30 lb bag of dog food <br class='md:hidden'/> has a carbon footprint of <br class='md:hidden'/> about 200 kg CO<sub>2</sub>e</h3>
                  <p>That's equivalent to the carbon emissions from driving a car 509 miles. Most of that comes from meat production and processing.
                  <span data-span></span></p>
                `,
            ctaLabel: "Next Question",
        },
    },
    // q5
    {
        kind: "questionIntro",
        id: "q5-intro",
        route: "/q5-intro",
        headline:
            "Let's move on to a breakfast <span data-anim='pulse'>favorite</span>.",
        questionId: "q5",
    },
    {
        kind: "question",
        id: "q5",
        route: "/q5",
        questionId: "q5",
        category: "Yogurt",
        questionText:
            "What is the carbon footprint of <br class='md:hidden'/> a 6 oz cup of yogurt?​",
        hint: "HINT: One mile of driving releases about 0.37 kg CO<sub>2</sub>e.",
        image: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/yogurt.webp",
            alt: "bowl of yogurt",
        },
        scoreImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/spotlight_yogurt.webp",
            alt: "diaper",
        },
        choices: [
            { label: "0.78 kg", isCorrect: false },
            {
                label: "0.145 kg",
                isCorrect: false,
            },
            {
                label: "0.092 kg",
                isCorrect: false,
            },
            {
                label: "0.374 kg",
                isCorrect: true,
            },
        ],
        correctValue: "0.374 kg",
        feedback: {
            headlineCorrect: "Right On!",
            headlineIncorrect: "Try Again!",
            spanCorrect: "Smooth Answer!",
            spanIncorrect:
                "Don't strain yourself—we've got another question coming!",
            image: {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/yogurt.webp",
                alt: "bowl of yogurt",
            },
            bodyHtml: `
                  <h3>A typical 6 oz cup of yogurt <br class='md:hidden'/> has a carbon footprint of <br class='md:hidden'/> about 0.374 kg CO<sub>2</sub>e</h3>
                  <p>That's equivalent to driving an average gasoline-powered car one mile. Most of that comes from milk production and processing. <span data-span></span></p>
                `,
            ctaLabel: "Next Question",
        },
    },
    // q6
    {
        kind: "questionIntro",
        id: "q6-intro",
        route: "/q6-intro",
        headline:
            "Let's <span data-anim='power-up'>power up</span> with a question about a common piece of tech.",
        questionId: "q6",
    },
    {
        kind: "question",
        id: "q6",
        route: "/q6",
        questionId: "q6",
        category: "Laptop",
        questionText:
            "What is the carbon footprint of <br class='md:hidden'/> a typical laptop?​",
        hint: " HINT: 100 miles of driving is equal to about 40 kg CO<sub>2</sub>e.",
        image: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/laptop.webp",
            alt: "laptop",
        },
        scoreImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/spotlight_laptop.webp",
            alt: "laptop",
        },
        choices: [
            { label: "350 kg", isCorrect: true },
            {
                label: "130 kg",
                isCorrect: false,
            },
            {
                label: "710 kg",
                isCorrect: false,
            },
            {
                label: "885 kg",
                isCorrect: false,
            },
        ],
        correctValue: "350 kg",
        feedback: {
            headlineCorrect: "Outstanding!",
            headlineIncorrect: "Oops!",
            spanCorrect: "You've got some energy for this quiz!",
            spanIncorrect: "Let's reboot for the next round.",
            image: {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/laptop.webp",
                alt: "laptop",
            },
            bodyHtml: `
                  <h3>A laptop racks up about <br class='md:hidden'/> 350 kg CO<sub>2</sub>e over its lifetime</h3>
                  <p>That's equivalent to driving a car 891 miles. Most of that comes from manufacturing the chips and screen. <span data-span></span></p>
                `,
            ctaLabel: "Next Question",
        },
    },
    // q7
    {
        kind: "questionIntro",
        id: "q7-intro",
        route: "/q7-intro",
        headline:
            "Let's <span data-anim='wrap'>wrap</span> things up with one last question.",
        questionId: "q7",
    },
    {
        kind: "question",
        id: "q7",
        route: "/q7",
        questionId: "q7",
        category: "Diapers",
        questionText:
            "What is the carbon footprint of <br class='md:hidden'/> a single disposable diaper?",
        hint: "HINT: Charging your phone once releases <br class='md:hidden'/> about 0.0125 kg CO<sub>2</sub>e.",
        image: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/diaper.webp",
            alt: "diaper",
        },
        scoreImage: {
            url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/spotlight_diaper.webp",
            alt: "diaper",
        },
        choices: [
            { label: "0.18 kg", isCorrect: false },
            {
                label: "0.089 kg",
                isCorrect: true,
            },
            {
                label: "0.041 kg",
                isCorrect: false,
            },
            {
                label: "0.582 kg",
                isCorrect: false,
            },
        ],
        correctValue: "0.089 kg",
        feedback: {
            headlineCorrect: "Well Done!",
            headlineIncorrect: "Good Guess, But Nope!",
            image: {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/diaper.webp",
                alt: "diaper",
            },
            bodyHtml: `
                  <h3>A single diaper has <br class='md:hidden'/> a carbon footprint of <br class='md:hidden'/> about 0.089 kg CO<sub>2</sub>e</h3>
                  <p>That's equivalent to charging your phone seven times. Most of that comes from manufacturing and the materials like plastics and pulp. </p>
                  <p>Let's see how you did on the quiz.</p>
                `,
            ctaLabel: "See Final Score",
        },
    },

    // Score
    {
        kind: "scoreSlide",
        id: "score",
        route: "/score",
        headline: "{score} out of {total}",
        body: `
        <p>Thanks for playing! We hope you learned something new about the carbon footprint of everyday products.</p>
        `,
        resourcesLabel: "Visit Our Resources",
        resourcesUrl: "/resources",
        shareLabel: "Share the Quiz",
        resourcesBlock: {
            headline: "Explore Our Resources",
            items: [
                {
                    image: {
                        url: "https://d1axg9pbn00pk6.cloudfront.net/resources/blog/liverpool-fc-x-1pointfive-collaboration/LFCx1P5_FullColor.webp",
                        alt: "LFC",
                    },
                    headline: "Carbon Removal in Apparel",
                    copy: "How Liverpool Football Club launched a new line of decarbonized products with 1PointFive.",
                    ctaLabel: "See the Story",
                    ctaUrl: "https://1pointfive.com/blog/decarbonizing-sports-apparel-what-the-liverpool-fc-x-1pointfive-collaboration-means-for-the-game-and-its-supporters",
                },
                {
                    image: {
                        url: "https://d1axg9pbn00pk6.cloudfront.net/resources/blog/decarbonizing-consumer-goods/Cover_FullColor.webp",
                        alt: "Warehouse",
                    },
                    headline: "The Business Case for DAC",
                    copy: "How carbon removed products can meet consumer expectations. ",
                    ctaLabel: "Read the Blog",
                    ctaUrl: "https://1pointfive.com/blog/decarbonizing-consumer-goods-the-role-of-direct-air-capture",
                },
                {
                    image: {
                        url: "https://d1axg9pbn00pk6.cloudfront.net/resources/blog/the-economic-benefits-of-carbon-dioxide-removal/EconomicBenefits_FullColor.webp",
                        alt: "LFC",
                    },
                    headline: "Economic Benefits of CDR",
                    copy: "Learn how CDR credits can help deliver economic value. ",
                    ctaLabel: "Explore More",
                    ctaUrl: "https://1pointfive.com/blog/the-economic-benefits-of-carbon-dioxide-removal",
                },
            ],
        },
    },

    // Share
    {
        kind: "shareSlide",
        id: "share",
        route: "/share",
        headline: "Share the Quiz!",
        body: `
            <p>Now that you've learned a bit about the carbon footprint of everyday products, share it
            with friends! Together, we can help more people understand the carbon impact of the
            products we use every day.</p>
    `,
        images: [
            {
                url: "https://d1axg9pbn00pk6.cloudfront.net/quiz/share/Share_1.webp",
                title: "",
            },
        ],

        shareMessage:
            "I took the quiz on carbon in consumer products and learned new facts about the carbon impact of the items we use every day. Take the quiz and see how high you can score!",
        ctaLabel: "Share the Quiz",
        // shareUrlOverride: "https://www.1pointfive.com/quiz",
    },
];
